import { Box, Card, CardHeader } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  FC, useEffect, useMemo, useRef,
} from 'react';
import moment from 'moment-timezone';
import Chart, { useChart } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { TDorLocationMetric, TMember } from '../../../../types';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getOrdersByDates } from '../../../../store/orders/selectors';
import { getCurrencyByCode } from '../../../../store/common/selectors';
import { AVATAR_FOLDER } from '../../../../data';
import { FOOT_CATEGORIES } from '../data/data';
import { useStats } from '../../DailyStats/hooks/useStats';
import { getAuthenticateUser } from '../../../../store/authenticate/selectors';

type TFootTrafficChart = {
  data: Array<TDorLocationMetric>;
  locationId: number
};

const FootTrafficChart: FC<TFootTrafficChart> = ({ data, locationId }) => {
  const { t } = useTranslation(['dashboard', 'orders', 'common']);
  const chartRef = useRef<any>(null);
  const isChartMounted = useRef(false);
  const ordersByDates = useAppSelector(getOrdersByDates);
  const getCurrency = useAppSelector(getCurrencyByCode);
  const { shop } = useAppSelector(getAuthenticateUser);

  const {
    calcOrdersByHours,
  } = useStats();

  const ordersByHours = calcOrdersByHours(ordersByDates);

  const chartData = useMemo(() => {
    if (data?.length || ordersByHours[locationId]) {
      const footTrafficCounts = new Array(24).fill(0);

      data?.forEach((item) => {
        const hour = moment.tz(item.start, shop?.shopTimezone).hour();
        footTrafficCounts[hour] += item?.footTraffic?.count || 0;
      });

      const salesByHour = new Array(24).fill(0);
      const salesByHourDetails = new Array(24).fill(null).map(() => ({})) as unknown as { [key: string]: { [key: string]: {
        sales: number,
        member: TMember
      } } };

      const hourlyData = ordersByHours[locationId] || {};
      Object.keys(hourlyData)?.forEach((hour) => {
        const startHour = parseInt(hour.split(':')[0], 10);
        const momentHour = moment.tz({ hour: startHour }, shop?.shopTimezone);
        const hourIndex = momentHour.hour();

        if (!Number.isNaN(hourIndex)) {
          hourlyData[hour].forEach((order) => {
            salesByHour[hourIndex] += parseFloat(order.netSales) || 0;

            const memberName = order.member?.name || 'Unknown';
            salesByHourDetails[hourIndex][memberName] = {
              sales: (salesByHourDetails[hourIndex][memberName]?.sales || 0) + parseFloat(order.netSales),
              member: order.member,
            };
          });
        }
      });

      return {
        footTrafficCounts,
        salesByHour,
        salesByHourDetails,
      };
    }
    return {};
  }, [data, locationId, ordersByHours]);

  const series = useMemo(() => [
    {
      name: t('sales'),
      type: 'column',
      fill: 'solid',
      data: chartData?.salesByHour || [],
    },
    {
      name: t('footTraffic', { ns: 'orders' }),
      type: 'line',
      fill: 'solid',
      data: chartData?.footTrafficCounts || [],
    },
  ], [chartData]);

  useEffect(() => {
    if (isChartMounted.current && chartRef.current?.updateOptions) {
      try {
        chartRef.current.updateOptions({
          fill: {
            type: series?.map((i) => i?.fill) as string[],
          },
          labels: FOOT_CATEGORIES?.map((label) => label || '') || [],
          tooltip: {
            shared: true,
            intersect: false,
            custom({
              dataPointIndex,
            }: any) {
              const footTraffic = chartData?.footTrafficCounts?.[dataPointIndex] || 0;
              const sales = chartData?.salesByHour?.[dataPointIndex] || 0;
              const memberSales = chartData?.salesByHourDetails?.[dataPointIndex] || {};

              let tooltipHTML = `
      <div style="padding: 10px; background-color: #fff; border-radius: 8px; box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1); font-size: 14px; min-width: 320px;">
        <div style="font-weight: bold; margin-bottom: 5px;">${sales.toFixed(2)}${getCurrency().mark} ${t('for', { ns: 'orders' })} ${footTraffic} ${t('visitors', { ns: 'orders' })}</div>
        <hr style="margin: 8px 0;" />
    `;

              if (Object.keys(memberSales).length > 0) {
                tooltipHTML += '<div>';
                Object.entries(memberSales).forEach(([memberName, memberData]) => {
                  if (!memberName || !memberData.member) {
                    console.warn('Invalid member data:', memberName, memberData);
                    return;
                  }

                  const { avatarFile } = memberData.member;
                  const initials = memberName?.split(' ')
                    .map((word) => word[0])
                    .join('')
                    .toUpperCase()
                    .slice(0, 2);

                  tooltipHTML += `
          <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              ${
  avatarFile
    ? `<img src="${AVATAR_FOLDER}${avatarFile}" alt="${memberName}" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover;" />`
    : `<div style="width: 24px; height: 24px; background-color: #454F5B; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold;">
                    ${initials}
                    </div>`
}
              <span style="color: #555;">${memberName}</span>
            </div>
            <span style="font-weight: bold;">${memberData.sales?.toFixed(2)}${getCurrency().mark}</span>
          </div>
        `;
                });
                tooltipHTML += '</div>';
              } else {
                tooltipHTML += `<div style="color: #888;">${t('noSalesForThisHour', { ns: 'orders' })}</div>`;
              }

              tooltipHTML += '</div>';
              return tooltipHTML;
            },
          },
        }, true, true);
      } catch (error) {
        console.error('Failed to update chart options:', error);
      }
    }
  }, [chartData, series, locationId]);

  const chartOptions = useChart({
    chart: {
      events: {
        mounted: (chart) => {
          isChartMounted.current = true;
          chartRef.current = chart;
        },
      },
    },
    plotOptions: {
      bar: {
        columnWidth: '16%',
      },
    },
    markers: {
      size: [0, 5],
      strokeColors: ['#fff'],
      strokeWidth: 2,
    },
    fill: {
      type: series?.length ? series?.map((i) => i?.fill) as string[] : [],
    },
    xaxis: {
      type: 'category',
      categories: FOOT_CATEGORIES,
    },
    yaxis: [
      {
        labels: {
          formatter: (value: number) => `${value.toFixed(2)} ${getCurrency().mark}`,
        },
        title: {
          text: '',
        },
      },
      {
        opposite: true,
        labels: {
          formatter: (value: number) => `${value.toFixed(2)}`,
        },
        title: {
          text: '',
        },
      },
    ],
    tooltip: {
      shared: true,
      intersect: false,
      custom({
        dataPointIndex,
      }) {
        const footTraffic = chartData?.footTrafficCounts?.[dataPointIndex] || 0;
        const sales = chartData?.salesByHour?.[dataPointIndex] || 0;
        const memberSales = chartData?.salesByHourDetails?.[dataPointIndex] || {};

        let tooltipHTML = `
          <div style="padding: 10px; background-color: #fff; border-radius: 8px; box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1); font-size: 14px; min-width: 320px;">
            <div style="font-weight: bold; margin-bottom: 5px;">${sales.toFixed(2)}${getCurrency().mark} ${t('for', { ns: 'orders' })} ${footTraffic} ${t('visitors', { ns: 'orders' })}</div>
            <hr style="margin: 8px 0;" />
        `;

        if (Object.keys(memberSales).length > 0) {
          tooltipHTML += '<div>';
          Object.entries(memberSales).forEach(([memberName, memberData]) => {
            const { avatarFile } = memberData.member;
            const initials = memberName?.split(' ')
              .map((word) => word[0])
              .join('')
              .toUpperCase()
              .slice(0, 2);

            tooltipHTML += `
              <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  ${
  avatarFile
    ? `<img src="${AVATAR_FOLDER}${avatarFile}" alt="${memberName}" style="width: 24px; height: 24px; border-radius: 50%; object-fit: cover;" />`
    : `<div style="width: 24px; height: 24px; background-color: #454F5B; color: #fff; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: bold;">
                        ${initials}
                        </div>`
}
                  <span style="color: #555;">${memberName}</span>
                </div>
                <span style="font-weight: bold;">${memberData.sales?.toFixed(2)}${getCurrency().mark}</span>
              </div>
            `;
          });
          tooltipHTML += '</div>';
        } else {
          tooltipHTML += `<div style="color: #888;">${t('noSalesForThisHour', { ns: 'orders' })}</div>`;
        }

        tooltipHTML += '</div>';
        return tooltipHTML;
      },
    },
  });

  return (
    <Card>
      <CardHeader title={t('dailyFootTraffic', { ns: 'orders' })} />
      <Box
        {...(!series[0]?.data?.length && !series[1]?.data?.length && {
          height: 400,
        })}
        sx={{ mx: 3 }}
        dir="ltr"
      >
        {series[0]?.data?.length && series[1]?.data?.length ? (
          <Chart type="line" series={series} options={chartOptions} height={364} />
        ) : (
          <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />
        )}
      </Box>
    </Card>
  );
};

export { FootTrafficChart };
